<template>
    <DialogLayout :class-name="'meeting-dialog'" @close="handleClose">
        <template #title>{{ dialogTitle }}</template>
        <template #default>
            <el-form ref="formRef" :model="formData" :rules="rules" label-position="top" class="meeting-form">
                <!-- 面谈时间：日期 + 开始~结束时间合并为一个输入框（与面接日程弹窗同一套控件） -->
                <el-form-item :label="t('employee.meeting.meetingTime')" prop="meetingDate" required>
                    <div class="slot-input-container">
                        <el-date-picker v-model="formData.meetingDate" type="date" placeholder="YYYY-MM-DD"
                            :format="dateDisplayFormat" value-format="YYYY-MM-DD" :prefix-icon="''" :clearable="false"
                            class="slot-date-picker" @change="handleTimeChange" />
                        <span class="slot-separator">|</span>
                        <StepTimePicker v-model="formData.startTime" :show-icon="false" class="slot-time-picker"
                            :editable="true" :auto-commit="true" :minute-options="[0, 15, 30, 45]"
                            @change="handleStartTimeChange" />
                        <span class="slot-separator">~</span>
                        <StepTimePicker v-model="formData.endTime" :show-icon="false" class="slot-time-picker"
                            :editable="true" :auto-commit="true" :hour-options="endHourOptions"
                            :minute-options="getEndMinuteOptions" @change="handleTimeChange" />
                    </div>
                </el-form-item>

                <el-form-item :label="t('employee.meeting.mode')" prop="isAiInterview">
                    <el-radio-group v-model="formData.isAiInterview">
                        <el-radio :value="true">{{ t('employee.meeting.aiInterview') }}</el-radio>
                        <el-radio :value="false">{{ t('employee.meeting.manualInterview') }}</el-radio>
                    </el-radio-group>
                </el-form-item>

                <!-- AI 面谈：担当者固定为 AI Agent，不需要会议链接 -->
                <el-form-item v-if="formData.isAiInterview" :label="t('employee.meeting.owner')">
                    <span class="static-value">{{ t('employee.meeting.aiAgent') }}</span>
                </el-form-item>
                <el-form-item v-else :label="t('employee.meeting.owner')" prop="ownerId">
                    <el-select v-model="formData.ownerId" filterable clearable
                        :placeholder="t('employee.form.placeholder.select')">
                        <el-option v-for="option in ownerOptions" :key="option.value" :label="option.label"
                            :value="option.value" />
                    </el-select>
                </el-form-item>

                <el-form-item v-if="!formData.isAiInterview" :label="t('employee.meeting.meetingUrl')" prop="meetingUrl">
                    <el-input v-model="formData.meetingUrl" class="url-input"
                        :placeholder="t('employee.form.placeholder.input')" :maxlength="300" />
                </el-form-item>

                <el-form-item :label="t('employee.meeting.memo')" prop="remark">
                    <el-input v-model="formData.remark" :placeholder="t('employee.form.placeholder.input')"
                        :maxlength="200" />
                </el-form-item>
            </el-form>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">{{ t('common.cancel') }}</el-button>
            <el-button type="primary" @click="handleConfirm">{{ confirmButtonText }}</el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import 'dayjs/locale/zh-cn'
import StepTimePicker from '@/components/basic-components/step-time-picker/index.vue'
import DialogLayout from './dialog-layout.vue'
import type { MeetingDialogProps, MeetingFormData } from './meeting-dialog-types'
import { getCandidateAccountApi } from '@/api/candidate'
const { t, locale } = useI18n()

// 日期展示带星期：2025-11-02 (日)
const dateDisplayFormat = 'YYYY-MM-DD (ddd)'
const syncDayjsLocale = () => {
    const isJa = String(locale.value || '').toLowerCase().startsWith('ja')
    dayjs.locale(isJa ? 'ja' : 'zh-cn')
}
syncDayjsLocale()
watch(locale, syncDayjsLocale)

const props = defineProps<MeetingDialogProps>()

const emit = defineEmits<{
    (e: 'confirm', data: MeetingFormData): void
    (e: 'cancel'): void
    (e: 'close'): void
}>()

const formRef = ref<FormInstance>()
const isEditMode = computed(() => !!props.data)
const dialogTitle = computed(() => isEditMode.value ? t('employee.meeting.edit') : t('employee.meeting.create'))
const confirmButtonText = computed(() => isEditMode.value ? t('common.save') : t('employee.meeting.createConfirm'))

const formData = reactive<MeetingFormData>({
    meetingDate: '',
    startTime: '',
    endTime: '',
    // 新建默认 AI 面谈
    isAiInterview: true,
    ownerId: undefined,
    ownerName: '',
    meetingUrl: '',
    remark: '',
})

// 结束时间不能早于开始时间：限制可选的小时与分钟
const endHourOptions = computed(() => {
    const [startHour] = (formData.startTime || '00:00').split(':').map(Number)
    const hours: number[] = []
    for (let hour = startHour; hour <= 23; hour++) {
        hours.push(hour)
    }
    return hours
})

const getEndMinuteOptions = (hour: number): number[] => {
    const minutes = [0, 15, 30, 45]
    if (!formData.startTime) return minutes
    const [startHour, startMinute] = formData.startTime.split(':').map(Number)
    // 与开始时间同小时时，只能选更晚的分钟
    return hour === startHour ? minutes.filter(minute => minute > startMinute) : minutes
}

// 规则里必须带上 required，否则 el-form-item 的 required 属性会额外补一条
// 没有 message 的 { required: true }，报错时会显示 async-validator 的默认英文文案
const rules = computed<FormRules>(() => ({
    meetingDate: [
        {
            required: true,
            message: t('employee.meeting.timeRequired'),
            validator: (_rule, _value, callback) => {
                if (!formData.meetingDate || !formData.startTime || !formData.endTime) {
                    callback(new Error(t('employee.meeting.timeRequired')))
                    return
                }
                callback()
            },
            trigger: 'change',
        },
    ],
}))

const ownerOptions = ref<Array<{ label: string; value: number }>>([])

async function fetchOwnerOptions() {
    try {
        const list = await getCandidateAccountApi({ userType: 'responsible' })
        ownerOptions.value = (list || []).map((item: { id: number; name: string }) => ({
            label: item.name,
            value: item.id,
        }))
    } catch {
        ownerOptions.value = []
    }
}

const handleTimeChange = () => {
    formRef.value?.validateField('meetingDate').catch(() => { })
}

// 开始时间变化后，结束时间早于开始时间则清空，避免出现倒序区间
const handleStartTimeChange = () => {
    if (formData.endTime && formData.startTime && formData.endTime <= formData.startTime) {
        formData.endTime = ''
    }
    handleTimeChange()
}

onMounted(() => {
    fetchOwnerOptions()
    if (props.data) {
        Object.assign(formData, props.data)
    }
})

const handleConfirm = async () => {
    try {
        await formRef.value?.validate()
    } catch {
        return
    }
    // AI 面谈不提交担当者和会议链接
    emit('confirm', formData.isAiInterview
        ? { ...formData, ownerId: undefined, ownerName: t('employee.meeting.aiAgent'), meetingUrl: '' }
        : { ...formData, ownerName: ownerOptions.value.find(option => option.value === formData.ownerId)?.label || '' })
}

const handleCancel = () => emit('cancel')
const handleClose = () => emit('close')
</script>

<style scoped lang="scss">
.meeting-form {
    // AI 面谈时担当者只读展示
    .static-value {
        font-size: 14px;
        line-height: 22px;
        color: #495057;
    }

    :deep(.el-form-item__label) {
        font-size: 14px;
        font-weight: 500;
        color: #212429;
        margin-bottom: 8px;

        &::before {
            display: none;
        }
    }

    // 必填星号跟在 label 文字后面
    :deep(.el-form-item.is-required > .el-form-item__label::after) {
        content: '*';
        color: var(--el-color-danger);
        margin-left: 4px;
    }

    // 日期 + 起止时间合并为一个输入框（样式同面接日程弹窗的 slot-input-container）
    .slot-input-container {
        width: 300px;
        display: flex;
        align-items: center;
        gap: 6px;
        min-height: 32px;
        padding: 0 10px;
        background-color: #F8F9FF;
        border-radius: 4px;

        // 注：el-date-picker 的根元素拿不到 scoped 的 data-v 属性，
        // 它的宽度与边框覆盖只能写在下方的全局样式块里
        .slot-time-picker {
            flex-shrink: 0;
        }

        .slot-separator {
            font-size: 13px;
            color: #C9D3D9;
            white-space: nowrap;
            flex-shrink: 0;
        }
    }

    .url-input {
        :deep(.el-input__inner) {
            color: var(--el-color-primary);
        }
    }
}
</style>

<style lang="scss">
.meeting-dialog {
    width: 512px !important;

    // 日期选择器的宽度与边框/背景只能用全局样式覆盖（scoped 选择器匹配不到它的根元素）
    .slot-input-container {
        .slot-date-picker {
            width: 132px !important;
            min-width: 132px !important;
            box-shadow: none !important;
            border: none !important;
            background-color: transparent !important;

            .el-input__wrapper {
                box-shadow: none !important;
                padding: 0 !important;
                background-color: transparent !important;
                border: none !important;
            }

            .el-input__inner {
                height: 30px !important;
                padding: 0 !important;
                border: none !important;
                font-size: 13px !important;
                color: #495057;
                background-color: transparent !important;
            }
        }
    }

    .dialog-footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;

        .el-button {
            min-width: 80px;
        }
    }
}
</style>
